import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { CheckCircle2, XCircle, Loader2, Trash2, AlertCircle } from "lucide-react"
import { SUBJECTS, getYears } from "@/lib/utils"
import type { FileWithMetadata } from "@/lib/hooks/useBatchUpload"

interface FileQueueTableProps {
  files: FileWithMetadata[]
  onUpdateMetadata: (id: string, updates: Partial<FileWithMetadata["metadata"]>) => void
  onRemoveFile: (id: string) => void
}

const years = getYears()

export function FileQueueTable({ files, onUpdateMetadata, onRemoveFile }: FileQueueTableProps) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success":
        return <CheckCircle2 className="h-5 w-5 text-green-500" />
      case "error":
        return <XCircle className="h-5 w-5 text-red-500" />
      case "uploading":
        return <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
      default:
        return <div className="h-5 w-5 rounded-full border-2 border-muted-foreground" />
    }
  }


  const hasValidationIssue = (file: FileWithMetadata) => {
    return !file.metadata.title || !file.metadata.year || !file.metadata.region || !file.metadata.subject
  }

  if (files.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-12 text-center">
        <p className="text-muted-foreground">暂无文件，请拖拽或选择文件</p>
      </div>
    )
  }

  return (
    <div className="rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">状态</TableHead>
            <TableHead className="min-w-[200px]">文件名</TableHead>
            <TableHead className="min-w-[200px]">标题</TableHead>
            <TableHead className="w-[120px]">年份</TableHead>
            <TableHead className="min-w-[150px]">地区</TableHead>
            <TableHead className="w-[120px]">科目</TableHead>
            <TableHead className="w-[80px]">信心</TableHead>
            <TableHead className="w-[80px]">操作</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {files.map((file) => (
            <TableRow key={file.id} className={file.status === "error" ? "bg-red-50/50" : ""}>
              <TableCell>{getStatusIcon(file.status)}</TableCell>

              <TableCell>
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm">{file.file.name}</span>
                  {hasValidationIssue(file) && file.status === "pending" && (
                    <AlertCircle className="h-4 w-4 flex-shrink-0 text-amber-500" />
                  )}
                </div>
              </TableCell>

              <TableCell>
                <Input
                  value={file.metadata.title || ""}
                  onChange={(e) => onUpdateMetadata(file.id, { title: e.target.value })}
                  disabled={file.status === "uploading" || file.status === "success"}
                  className="h-8 text-sm"
                  placeholder="试卷标题"
                />
              </TableCell>

              <TableCell>
                <Select
                  value={file.metadata.year?.toString() || ""}
                  onValueChange={(value) => onUpdateMetadata(file.id, { year: parseInt(value) })}
                  disabled={file.status === "uploading" || file.status === "success"}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="年份" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={y.toString()}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>

              <TableCell>
                <Input
                  value={file.metadata.region || ""}
                  onChange={(e) => onUpdateMetadata(file.id, { region: e.target.value })}
                  disabled={file.status === "uploading" || file.status === "success"}
                  className="h-8 text-sm"
                  placeholder="地区"
                />
              </TableCell>

              <TableCell>
                <Select
                  value={file.metadata.subject || ""}
                  onValueChange={(value) => onUpdateMetadata(file.id, { subject: value })}
                  disabled={file.status === "uploading" || file.status === "success"}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="科目" />
                  </SelectTrigger>
                  <SelectContent>
                    {SUBJECTS.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>

              <TableCell>
                <Badge variant={file.metadata.confidence && file.metadata.confidence >= 66 ? "default" : "secondary"}>
                  {file.metadata.confidence || 0}%
                </Badge>
              </TableCell>

              <TableCell>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onRemoveFile(file.id)}
                  disabled={file.status === "uploading"}
                  className="h-8 w-8"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {files.some(f => f.status === "error") && (
        <div className="border-t bg-red-50 p-3 text-sm">
          <div className="flex items-center gap-2 text-red-600">
            <XCircle className="h-4 w-4" />
            <span>部分文件上传失败，请检查错误信息</span>
          </div>
        </div>
      )}
    </div>
  )
}
